import React, { useEffect, useRef } from 'react'
import { noop } from 'lodash'
import { TOP_NAV_HEIGHT } from '~/common/scrollUtils'
import { WithRequiredKeys } from '~/common/types'
import { StickyHeightExpander, StickyHeightExpanderProps } from '~/components/v2/StickyHeightExpander'
import { useMsg, Msg } from '~/common/localizationUtils'
import { ignoreProviderMissingError } from '~/common/reactUtils'
import { $thm } from '~/common/themeSx'
import { RegionErrorBoundary } from '../../../components/empty_states/EmptyStates'
import {
  Box,
  BoxProps,
  ButtonProps,
  Checkbox,
  CheckboxProps,
  Flex,
  FlexProps,
  Heading,
  HeadingProps,
  LinkBox,
  LinkOverlay,
  Skeleton,
  StackProps,
  VStack,
  chakra,
  forwardRef,
  mergeRefs,
  VisuallyHidden
} from '../../../chakra'
import { KeyboardAccessibleGrid } from '../../../foundations/accessibility/KeyboardAccessibleGrid'
import { insetDivider, ScrollShadowBox } from '../../../foundations/data_display/Separators'
import { ListRow } from '../../../components/lists/ListItems'
import { SplitListContext, useSplitListContext } from './context'

type SplitListListProps = StackProps &
  Pick<StickyHeightExpanderProps, 'offsetTopPx'> & {
    preferredWidth?: string | number
  }

/**
 * SplitList.List
 *
 * The sidebar list wrapper for a split list sidebar. This component will maintain a sticky header on
 * the split list sidebar.
 *
 * This component accepts StackProps and an offsetTopPx as input params. The offsetTopPx is used to determine
 * the offset from the top that header should stick to. By default it's set to 0.
 *
 * See: /docs/design-system/modules/lists/split-list/split-list
 */
export const List = forwardRef<SplitListListProps, 'div'>(
  ({ children, offsetTopPx = TOP_NAV_HEIGHT, preferredWidth = '420px', ...props }, ref) => {
    // eslint-disable-next-line lines-around-directive
    'use no memo' // Disable React Compiler - ignoreProviderMissingError uses try-catch around hooks
    const [isMobile] = ignoreProviderMissingError(() => useSplitListContext.select(s => s?.isMobile))

    // On mobile, we don't want the sticky behavior, so we just use a Box
    const Component = isMobile ? Box : StickyHeightExpander
    return (
      <Component
        ref={ref}
        {...(isMobile ? {} : { offsetTopPx })}
        sx={$thm({
          display: 'flex',
          flexDir: 'column',
          gap: 0,
          bg: 'white',
          pr: { $canvas: 2 },
          w: {
            // StickyHeightExpander wraps the sx styles in the cq utility, so we can use container queries here
            '@splitList/default': preferredWidth,
            '@splitList/2xl': '45%',
            '@splitList/4xl': preferredWidth
          }
        })}
        {...props}>
        <RegionErrorBoundary>{children}</RegionErrorBoundary>
      </Component>
    )
  }
)

List.displayName = 'SplitList.List'

/**
 * SplitList.ListHeader
 *
 * The header wrapper for any sidebar list.
 *
 * This component accepts FlexProps as input params.
 *
 * See: /docs/design-system/modules/lists/split-list/split-list
 *
 * @agent-gotcha Title goes in SplitList.ListHeading (heading4). Optional trailing Button as second child. Don't wrap the title in Text.
 * @agent-gotcha For title + metadata, prefer TitleBlockTwoLine atoms with textSize='md'.
 */
export const ListHeader = forwardRef(({ sx, ...props }: FlexProps, ref) => (
  <Flex
    ref={ref}
    data-items-list-header
    data-sidebar-list-header
    as="header"
    w="full"
    bg="white"
    align="center"
    justifyContent="space-between"
    flexShrink={0}
    py={3}
    sx={{
      ...$thm({
        minH: { $base: '72px', $canvas: 0 },
        px: { $base: 6, $canvas: 4 },
        borderBottom: { $base: '1px solid var(--vndly-color-container-border)', $canvas: 'none' }
      }),
      ...sx
    }}
    {...props}
  />
))

ListHeader.displayName = 'SplitList.ListHeader'

/**
 * SplitList.ListHeading
 *
 * The heading text for a `SplitList.ListHeader`. Use this inside of `SplitList.ListHeader`.
 *
 * Matches CardHeading: heading4, semantic h3, m={0} so Bootstrap's
 * `h3 { margin-top: 20px }` does not leak onto Django pages without `.isChakra`.
 *
 * @agent-gotcha Put the sidebar title here, not as a bare child of SplitList.ListHeader and not in Text.
 */
export const ListHeading = forwardRef((props: HeadingProps, ref) => (
  <Heading as="h3" textStyle="heading4" data-sidebar-heading flexGrow={1} m={0} ref={ref} {...props} />
))

ListHeading.displayName = 'SplitList.ListHeading'

/**
 * SplitList.ListFooter
 *
 * The footer wrapper for any sidebar list.
 *
 * This component accepts FlexProps as input params.
 *
 * See: /docs/design-system/modules/lists/split-list/split-list
 *
 * @agent-gotcha Sidebar split-list action footer — submit/cancel Buttons or ButtonGroupActions only. NOT for helper copy or explanatory text.
 * @agent-gotcha Default w='420px' (override with width='full' if your sidebar isn't the standard width).
 */
export const ListFooter = forwardRef(({ sx, ...props }: FlexProps, ref) => (
  <Flex
    ref={ref}
    data-sidebar-footer
    data-items-list-footer
    as="footer"
    bg="white"
    w="420px"
    align="center"
    color="var(--vndly-color-container-subdued)"
    px={6}
    py={3}
    minH="72px"
    sx={{
      ...$thm({
        borderTop: { $base: '1px solid var(--vndly-color-container-border)', $canvas: 'none' }
      }),
      ...sx
    }}
    {...props}
  />
))

ListFooter.displayName = 'SplitList.ListFooter'

/**
 * Custom hook to scroll an element into view within a scrollable container
 * @param index - Index of the item to scroll into view
 * @param selector - CSS selector to find the target element (default: '[data-keyboard-grid-row]')
 * @param offset - Offset from the top of the container (default: 60)
 */
const useScrollElIntoView = (
  index: number | undefined,
  selector: string = '[data-keyboard-grid-row]',
  offset: number = 60
) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (index != null && index >= 0 && scrollContainerRef.current) {
      const container = scrollContainerRef.current
      const items = container.querySelectorAll(selector)
      const activeItem = items[index] as HTMLElement

      if (container && activeItem) {
        const containerRect = container.getBoundingClientRect()
        const itemRect = activeItem.getBoundingClientRect()
        const itemTop = itemRect.top - containerRect.top + container.scrollTop

        // Calculate scroll position to show item with offset from top
        const targetScrollTop = itemTop - offset

        // Ensure we don't scroll past the top
        const finalScrollTop = Math.max(0, targetScrollTop)

        // scrollTo doesn't seem to be declared in the jest environment
        if (container.scrollTo) {
          container.scrollTo({
            top: finalScrollTop,
            behavior: 'smooth'
          })
        } else {
          container.scrollTop = finalScrollTop
        }
      }
    }
  }, [index, selector, offset])

  return scrollContainerRef
}

type SplitListItemsProps = React.PropsWithChildren<{
  isLoading?: boolean
  skeletonCount?: number
  index?: number
  onChange?: (index: number) => void
  initialActiveColumn?: number
}> &
  WithRequiredKeys<Omit<BoxProps, 'onChange'>, 'aria-label'>

/**
 * SplitList.Items
 *
 * The sidebar list items wrapper for split list sidebars.
 *
 * This component accepts TabsProps and tab ref information, if neccessary, as input params.
 * When isLoading is true, it will show skeleton items instead of children.
 *
 * See: /docs/design-system/modules/lists/split-list/split-list
 */
export const Items = forwardRef<SplitListItemsProps, 'div'>(({ index, onChange, ...props }, ref) => {
  return (
    // The default behavior is to just inherit the context values from the higher up SplitListContext used in SplitList.
    // But! You theoretically don't have to use SplitList.List within the SplitList component, so in the off-chance
    // someone does that, we need to make sure the context is still provided and SplitList.Items can still
    // supply the index and onChange directly.
    <InheritingSplitListContext {...{ index, onChange }}>
      <ItemsInner ref={ref} {...props} />
    </InheritingSplitListContext>
  )
})

Items.displayName = 'SplitList.Items'

// Forwards the SplitListContext values if present, otherwise just makes sure the context is there if they are not provided directly
function InheritingSplitListContext({
  index,
  onChange,
  children
}: Pick<SplitListItemsProps, 'index' | 'onChange' | 'children'>) {
  // eslint-disable-next-line lines-around-directive
  'use no memo' // Disable React Compiler - ignoreProviderMissingError uses try-catch around hooks
  const [ctx] = ignoreProviderMissingError(useSplitListContext)
  if ((index !== undefined && ctx !== undefined) || (onChange !== undefined && ctx !== undefined)) {
    // eslint-disable-next-line no-console
    console.error(
      'Cannot provide the index/onChange props when SplitListContext is already present. ' +
        'Please pass the index/onChange props to the SplitList component instead.'
    )
  }

  return (
    <SplitListContext
      {...{
        index: index ?? ctx?.index,
        onChange: onChange ?? ctx?.onChange,
        isMobile: ctx?.isMobile ?? false,
        drawerIsOpen: ctx?.drawerIsOpen ?? false,
        setDrawerIsOpen: ctx?.setDrawerIsOpen ?? noop
      }}>
      {children}
    </SplitListContext>
  )
}

const ItemsInner = forwardRef<SplitListItemsProps, 'div'>(
  (
    {
      children,
      index: indexProp,
      onChange,
      isLoading = false,
      skeletonCount = 5,
      initialActiveColumn = 0,
      'aria-label': ariaLabel,
      ...props
    },
    ref
  ) => {
    const indexFromContext = useSplitListContext.select(s => s?.index)
    const index = indexProp ?? indexFromContext
    // Auto-scroll active item into view
    const scrollContainerRef = useScrollElIntoView(index)

    return (
      <ScrollShadowBox
        ref={mergeRefs(scrollContainerRef, ref)}
        data-items-list-items
        data-testid="items-list-items"
        w="full"
        {...props}>
        <KeyboardAccessibleGrid
          aria-label={ariaLabel}
          defaultActiveCell={index != null && index >= 0 ? { row: index, col: initialActiveColumn ?? 0 } : undefined}>
          {isLoading ? <ItemsSkeleton count={skeletonCount} /> : children}
        </KeyboardAccessibleGrid>
      </ScrollShadowBox>
    )
  }
)

/**
 * ItemsSkeleton
 *
 * Skeleton component for split list items. Shows a generic skeleton layout
 * that works well with different list item formats.
 *
 * @param count - Number of skeleton items to render
 */
export const ItemsSkeleton = ({ count = 5 }: { count?: number }) => {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <Item data-testid="sidebar-split-list-skeleton-item" key={`skeleton-${index}`} index={index} isLoading>
          <VStack gap={2} w="full" alignItems="flex-start" opacity={1 - index * 0.15}>
            <Skeleton height="14px" width="80px" />
            <Skeleton height="16px" width="90%" />
          </VStack>
        </Item>
      ))}
    </>
  )
}

export type SplitListItemProps = ButtonProps & {
  index: number
  isSelected?: boolean
  accessory?: React.ReactNode
  isLoading?: boolean
}

/**
 * SplitList.Item
 *
 * Single list item for split list sidebars. This component is already embedded in custom components to build
 * the various Split List Item formats. If a
 * new format of a split list item is needed, this component can be used as the wrapper for each list item.
 *
 * This component accepts BoxProps and an isSelected boolean as input params. The isSelected param can be set
 * when a list item tab is selected.
 *
 * See: /docs/design-system/modules/lists/split-list/split-list
 */
export const Item = forwardRef<SplitListItemProps, 'div'>(
  ({ children, index, isSelected = false, isLoading = false, accessory, ...props }, ref) => {
    return (
      <LinkBox
        ref={ref}
        borderColor="var(--vndly-color-listitem-border)"
        borderTopWidth="1px"
        _first={{ borderTopWidth: 0 }}
        _last={{ borderBottomWidth: 0 }}
        data-keyboard-grid-row
        data-selected={isSelected}
        w="full"
        sx={$thm({
          borderTopWidth: { $canvas: 0 },
          '&:not(:first-of-type)::before': insetDivider(),
          '&:hover::before, &[data-selected="true"]::before': { opacity: 0 },
          '&:hover + &::before, &[data-selected="true"] + &::before': { opacity: 0 }
        })}>
        <ListRow
          justifyContent="space-between"
          cursor="default"
          bg={
            !isSelected || isLoading
              ? 'var(--vndly-color-listitem-background)'
              : 'var(--vndly-color-listitem-background-selected)'
          }
          role="none"
          sx={$thm({
            px: { $canvas: 4 },
            py: { $canvas: 4 },
            borderRadius: { $canvas: 'xl' },
            '[data-using-mouse] &:hover': {
              bg: isSelected
                ? 'var(--vndly-color-listitem-background-selected-hover)'
                : 'var(--vndly-color-listitem-background-hover)'
            },
            ...(isSelected && {
              borderLeftWidth: { $base: '4px', $canvas: 0 },
              borderLeftColor: 'var(--vndly-color-listitem-selected-indicator-background)',
              pl: { $base: 'calc(1.5rem - 4px)', $canvas: 4 }
            })
          })}>
          <Flex flexDirection="row-reverse" align="stretch" w="full">
            <Box flex="1" data-keyboard-grid-cell={0}>
              <ButtonCell index={index} isSelected={isSelected} isLoading={isLoading} {...props}>
                {children}
              </ButtonCell>
            </Box>
            {accessory ? <Flex data-keyboard-grid-cell={1}>{accessory}</Flex> : null}
          </Flex>
        </ListRow>
      </LinkBox>
    )
  }
)

Item.displayName = 'SplitList.Item'

const ButtonCell = ({
  index,
  isSelected,
  isLoading,
  children,
  ...props
}: {
  index: number
  isSelected: boolean
  isLoading: boolean
} & ButtonProps) => {
  // eslint-disable-next-line lines-around-directive
  'use no memo' // Disable React Compiler - ignoreProviderMissingError uses try-catch around hooks
  const ref = useRef<HTMLButtonElement>(null)
  const [setDrawerIsOpen] = ignoreProviderMissingError(() => useSplitListContext.select(s => s?.setDrawerIsOpen))
  const [onChange] = ignoreProviderMissingError(() => useSplitListContext.select(s => s?.onChange))

  return (
    <LinkOverlay
      ref={ref}
      as="button"
      data-testid="split-list-item-button"
      type="button"
      aria-controls="split-list-content-container"
      aria-current={isSelected}
      // @ts-expect-error - Mixing LinkOverlay and button causes a type error
      onClick={() => {
        if (index != null && index >= 0) {
          onChange?.(index)
          setDrawerIsOpen?.(true)
          setTimeout(() => {
            const container = document.getElementById('split-list-content-container')
            if (!container) return
            const rect = container.getBoundingClientRect()
            const prevScrollY = window.scrollY
            container.focus() // Focus the element
            // But then we want to control the scroll position ourselves
            if (rect.top > TOP_NAV_HEIGHT) {
              // If the top of the container was already visible, don't scroll, aka put it back to where it was
              window.scrollTo({ top: prevScrollY, behavior: 'instant' })
            } else {
              // Otherwise scroll it to the top of the viewport, minus the top nav height
              const newPosition = window.scrollY + rect.top - TOP_NAV_HEIGHT
              window.scrollTo({ top: newPosition, behavior: 'instant' })
            }
          }, 0)
        }
      }}
      cursor={isSelected ? 'default' : 'pointer'}
      _focusVisible={{ outline: 'none', _before: { boxShadow: 'insetOutline' } }}
      textAlign="left"
      w="full"
      {...props}
      sx={$thm({
        ...props.sx,
        p: { $base: 2, $canvas: 0 },
        m: { $base: -2, $canvas: 0 }
      })}>
      <VisuallyHidden>
        <Msg id="common.ui.view_details_for_item" description="View details for " />
      </VisuallyHidden>
      {/* This is to allow the text to be selectable */}
      <chakra.span display="block" pos="relative" userSelect="text" zIndex={0}>
        {children}
      </chakra.span>
    </LinkOverlay>
  )
}

export type SplitListItemCheckboxProps = Omit<CheckboxProps, 'aria-label'> &
  (
    | {
        itemDescription?: never
        'aria-label': string
      }
    | {
        itemDescription: string
        'aria-label'?: never
      }
  )
/**
 * Checkbox component for split list sidebar items.
 *
 * Also the checkbox requires an itemDescription which should be the text content of the split list item.
 * It will be used to create the aria-label for the checkbox, e.g. "Added {itemDescription} to bulk selection".
 * You can also just overwrite aria-label completely if you prefer a custom aria-label.
 *
 * @example
 * <SplitList.Item
 *   isSelected={isSelected}
 *   accessory={
 *     <SplitList.ItemCheckbox
 *       itemDescription={description} // This is required, just pass the description of the item
 *       isChecked={isItemChecked(task_id)}
 *       onChange={() => toggleItem(task_id)}
 *     />
 *   }>
 *   <Text>{description}</Text> // The same text as the item description here
 * </SplitList.Item>
 */
export const ItemCheckbox = forwardRef<SplitListItemCheckboxProps, 'input'>(
  ({ 'aria-label': ariaLabel, itemDescription, ...props }, ref) => {
    const msg = useMsg()
    return (
      <Checkbox
        ref={ref}
        size="lg"
        mb={0}
        mr={4}
        zIndex={1}
        data-testid="split-list-item-checkbox"
        data-keyboard-grid-checkbox
        aria-label={
          ariaLabel ??
          (props.isChecked
            ? msg('common.ui.remove_from_bulk_selection', { itemDescription })
            : msg('common.ui.add_to_bulk_selection', { itemDescription }))
        }
        {...props}
      />
    )
  }
)

ItemCheckbox.displayName = 'SplitList.ItemCheckbox'
